import type React from 'react'
import styled from 'styled-components'

import type { ButtonProps } from '@components/ui/Buttons'
import { BUTTON_CSS_VARIABLES, IconButton } from '@components/ui/Buttons'
import { CircularProgress } from '@components/ui/CircularProgress'
import type { TooltipProps } from '@components/ui/Tooltips'
import { Tooltip } from '@components/ui/Tooltips'

const InlineBlockDiv = styled.div`
  display: inline-block;
  width: 36px;
  height: 36px;
  vertical-align: middle;
`

const StyledIconButton = styled(IconButton)`
  width: 34px;
  height: 34px;
  padding: 1px;
  color: ${({ theme }) => theme.colors.gray[100]};
  font-size: 16px;
  line-height: 20px;

  ${BUTTON_CSS_VARIABLES.color}: ${({ theme }) => theme.colors.gray[100]};
  ${BUTTON_CSS_VARIABLES.disabledColor}: ${({ theme }) => theme.colors.gray[40]};
`

type FunctionWithEvent = (event: React.MouseEvent<HTMLButtonElement>) => void

export interface TmsIconButtonProps extends Omit<TooltipProps, 'title' | 'children'> {
  className?: string
  disabled?: boolean
  icon: NonNullable<React.ReactNode>
  loading?: boolean
  onClick?: VoidFunction | FunctionWithEvent
  tooltipText?: React.ReactNode
  testId?: string
}

export function TmsIconButton({
  disabled = false,
  icon,
  loading = false,
  onClick,
  tooltipText,
  testId,
  ...rest
}: TmsIconButtonProps) {
  const iconButtonProps: Partial<ButtonProps> = {
    disabled: disabled || loading,
  }

  if (!disabled) {
    iconButtonProps.onClick = onClick
  }

  if (tooltipText) {
    return (
      <Tooltip title={tooltipText} {...rest}>
        <InlineBlockDiv>
          <StyledIconButton {...iconButtonProps} data-testid={testId}>
            {loading ? <CircularProgress color="inherit" size={18} /> : icon}
          </StyledIconButton>
        </InlineBlockDiv>
      </Tooltip>
    )
  }
  return (
    <StyledIconButton {...iconButtonProps} data-testid={testId}>
      {loading ? <CircularProgress color="inherit" size={18} /> : icon}
    </StyledIconButton>
  )
}
